import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TextField,
  FormControl, InputLabel, Select, MenuItem, Button, CircularProgress,
  Alert, InputAdornment, IconButton, Tooltip, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudIcon from '@mui/icons-material/Cloud';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { authGet, refreshCdnInstances, refreshCdnInstance } from '../../services/apiService';

const CDN_COLORS = {
  Cloudflare: { bg: '#e65100', text: '#fff' },
  Akamai:     { bg: '#1565c0', text: '#fff' },
  'No CDN':   { bg: '#616161', text: '#fff' },
};
const CDN_VALUES = ['Cloudflare', 'Akamai', 'No CDN'];

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const relativeTime = (d) => {
  if (!d) return '—';
  const mins = Math.round((Date.now() - new Date(d)) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const MetricCard = ({ label, value, sub, color, onClick, selected }) => (
  <Paper
    elevation={selected ? 3 : 1}
    onClick={onClick}
    sx={{
      px: 2, py: 1.5,
      borderLeft: `4px solid ${color}`,
      outline: selected ? `2px solid ${color}` : 'none',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s',
      '&:hover': onClick ? { bgcolor: `${color}10` } : {},
      height: '100%',
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color, lineHeight: 1 }}>{value}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{label}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// ── Main component ────────────────────────────────────────────────────────────
const CdnView = () => {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [refreshing, setRefreshing]   = useState(false);
  const [refreshMsg, setRefreshMsg]   = useState(null);

  const [search, setSearch]           = useState('');
  const [filterCdn, setFilterCdn]     = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterGroup, setFilterGroup]   = useState('');
  const [orderBy, setOrderBy]         = useState('Instance Name');
  const [order, setOrder]             = useState('asc');

  const [regionModal, setRegionModal] = useState({ open: false, cdnType: '', cdnLabel: '', color: '' });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  const [refreshingRow, setRefreshingRow] = useState(null); // instanceId being refreshed

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await authGet('/api/release-calendar/customer-instances', {}, 'json', { useCache: false });
      if (!r.success) throw new Error(r.message || 'Failed to load');
      setRows(r.data?.rows || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefreshCdn = async () => {
    try {
      setRefreshing(true);
      setRefreshMsg(null);
      const r = await refreshCdnInstances();
      if (r.success) {
        setRefreshMsg({ type: 'success', text: `CDN updated for ${r.updated} instance${r.updated !== 1 ? 's' : ''}` });
        await load();
      } else {
        setRefreshMsg({ type: 'error', text: r.message || 'Refresh failed' });
      }
    } catch (e) {
      setRefreshMsg({ type: 'error', text: e.message || 'Refresh failed' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefreshRow = async (instanceId) => {
    try {
      setRefreshingRow(instanceId);
      const r = await refreshCdnInstance(instanceId);
      if (r.success) {
        setRows(prev => prev.map(row =>
          row._id === instanceId
            ? { ...row, _cdn: r.cdn, _cdnUpdatedAt: r.cdnUpdatedAt }
            : row
        ));
      }
    } catch (_) {
      // silently fail — row just won't update
    } finally {
      setRefreshingRow(null);
    }
  };

  const getCdn  = (row) => row._cdn || '';
  const getName = (row) => (row['Friendly name'] || row['Instance Name'] || '').toString().trim();
  const getAltName = (row) => {
    const fn = (row['Friendly name'] || '').trim();
    const in_ = (row['Instance Name'] || '').trim();
    return fn && in_ && fn !== in_ ? in_ : '';
  };
  const getDomain = (row) => (row['Instance Domain'] || '').toString().trim();

  // Derived filter option lists
  const regionOptions = useMemo(() => [...new Set(rows.map(r => r['Region']).filter(Boolean))].sort(), [rows]);
  const groupOptions  = useMemo(() => [...new Set(rows.map(r => r['Group']).filter(Boolean))].sort(), [rows]);

  // Summary metrics
  const metrics = useMemo(() => ({
    total:      rows.length,
    cloudflare: rows.filter(r => getCdn(r) === 'Cloudflare').length,
    akamai:     rows.filter(r => getCdn(r) === 'Akamai').length,
    noCdn:      rows.filter(r => getCdn(r) === 'No CDN').length,
    notScanned: rows.filter(r => !getCdn(r)).length,
  }), [rows]);

  const pct = (n) => metrics.total > 0 ? ` · ${Math.round((n / metrics.total) * 100)}%` : '';

  // Filtered + sorted list
  const displayed = useMemo(() => {
    let list = [...rows];
    if (filterCdn === '__none__') {
      list = list.filter(r => !getCdn(r));
    } else if (filterCdn) {
      list = list.filter(r => getCdn(r) === filterCdn);
    }
    if (filterRegion) list = list.filter(r => r['Region'] === filterRegion);
    if (filterGroup)  list = list.filter(r => r['Group'] === filterGroup);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        getName(r).toLowerCase().includes(q) ||
        getDomain(r).toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      let va, vb;
      if (orderBy === '_cdn') { va = getCdn(a); vb = getCdn(b); }
      else { va = (a[orderBy] || '').toString(); vb = (b[orderBy] || '').toString(); }
      va = va.toLowerCase(); vb = vb.toLowerCase();
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, filterCdn, filterRegion, filterGroup, search, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('asc'); }
  };

  // Regional breakdown for modal — computed from ALL rows for the given CDN type
  const getRegionalBreakdown = (cdnType) => {
    const subset = cdnType === '__none__'
      ? rows.filter(r => !getCdn(r))
      : rows.filter(r => getCdn(r) === cdnType);
    const counts = {};
    subset.forEach(r => {
      const region = r['Region'] || 'Unknown';
      counts[region] = (counts[region] || 0) + 1;
    });
    const total = subset.length;
    return Object.entries(counts)
      .map(([region, count]) => ({
        region,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  };

  const openRegionModal = (cdnType, cdnLabel, color) => {
    setRegionModal({ open: true, cdnType, cdnLabel, color });
  };

  // Excel export of currently filtered rows
  const handleExport = () => {
    const data = displayed.map(row => ({
      'Instance Name':  row['Instance Name'] || '',
      'Friendly Name':  row['Friendly name'] || '',
      'Domain':         getDomain(row),
      'CDN':            getCdn(row) || 'Not Scanned',
      'Region':         row['Region'] || '',
      'Group':          row['Group'] || '',
      'Last Scanned':   row._cdnUpdatedAt ? fmtDateTime(row._cdnUpdatedAt) : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CDN View');
    XLSX.writeFile(wb, `cdn-view-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const SortCell = ({ col, label }) => (
    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', whiteSpace: 'nowrap', py: 1 }}>
      <TableSortLabel active={orderBy === col} direction={orderBy === col ? order : 'asc'} onClick={() => handleSort(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const activeFilters = [filterCdn, filterRegion, filterGroup, search.trim()].filter(Boolean).length;

  // Reset to page 0 whenever filters/sort change
  useMemo(() => { setPage(0); }, [filterCdn, filterRegion, filterGroup, search, orderBy, order]);

  const paginated = useMemo(
    () => displayed.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [displayed, page]
  );

  // Regional modal breakdown data
  const modalBreakdown = useMemo(
    () => regionModal.open ? getRegionalBreakdown(regionModal.cdnType) : [],
    [regionModal.open, regionModal.cdnType, rows]
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {refreshMsg && (
        <Alert severity={refreshMsg.type} onClose={() => setRefreshMsg(null)} sx={{ mb: 2 }}>
          {refreshMsg.text}
        </Alert>
      )}
      {refreshing && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {/* Summary cards — clickable to filter + open regional modal */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md>
          <MetricCard label="Total Instances" value={metrics.total} color="#1D4434" />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <MetricCard
            label="Cloudflare" value={metrics.cloudflare}
            sub={`of total${pct(metrics.cloudflare)} · click for regional breakdown`}
            color="#e65100"
            selected={filterCdn === 'Cloudflare'}
            onClick={() => {
              setFilterCdn(f => f === 'Cloudflare' ? '' : 'Cloudflare');
              openRegionModal('Cloudflare', 'Cloudflare', '#e65100');
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <MetricCard
            label="Akamai" value={metrics.akamai}
            sub={`of total${pct(metrics.akamai)} · click for regional breakdown`}
            color="#1565c0"
            selected={filterCdn === 'Akamai'}
            onClick={() => {
              setFilterCdn(f => f === 'Akamai' ? '' : 'Akamai');
              openRegionModal('Akamai', 'Akamai', '#1565c0');
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <MetricCard
            label="No CDN" value={metrics.noCdn}
            sub={`of total${pct(metrics.noCdn)} · click for regional breakdown`}
            color="#616161"
            selected={filterCdn === 'No CDN'}
            onClick={() => {
              setFilterCdn(f => f === 'No CDN' ? '' : 'No CDN');
              openRegionModal('No CDN', 'No CDN', '#616161');
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <MetricCard
            label="Not Scanned" value={metrics.notScanned}
            sub="CDN unknown · click for regional breakdown"
            color="#9e9e9e"
            selected={filterCdn === '__none__'}
            onClick={() => {
              setFilterCdn(f => f === '__none__' ? '' : '__none__');
              openRegionModal('__none__', 'Not Scanned', '#9e9e9e');
            }}
          />
        </Grid>
      </Grid>

      {/* Distribution bar */}
      {metrics.total > 0 && (
        <Paper elevation={1} sx={{ px: 2, py: 1.5, mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>CDN Distribution</Typography>
          <Box sx={{ display: 'flex', height: 16, borderRadius: 2, overflow: 'hidden', gap: '2px' }}>
            {[
              { key: 'Cloudflare', n: metrics.cloudflare, color: '#e65100' },
              { key: 'Akamai',     n: metrics.akamai,     color: '#1565c0' },
              { key: 'No CDN',     n: metrics.noCdn,      color: '#616161' },
              { key: 'Unknown',    n: metrics.notScanned,  color: '#e0e0e0' },
            ].filter(s => s.n > 0).map(s => (
              <Tooltip key={s.key} title={`${s.key}: ${s.n}`} arrow>
                <Box sx={{ flex: s.n, bgcolor: s.color, transition: 'flex 0.3s' }} />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.75, flexWrap: 'wrap' }}>
            {[
              { key: 'Cloudflare', n: metrics.cloudflare, color: '#e65100' },
              { key: 'Akamai',     n: metrics.akamai,     color: '#1565c0' },
              { key: 'No CDN',     n: metrics.noCdn,      color: '#616161' },
              { key: 'Unknown',    n: metrics.notScanned,  color: '#9e9e9e' },
            ].map(s => (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                <Typography variant="caption" color="text.secondary">{s.key} ({s.n})</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Filters + actions */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search name or domain…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>CDN Type</InputLabel>
          <Select value={filterCdn} label="CDN Type" onChange={e => setFilterCdn(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {CDN_VALUES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            <MenuItem value="__none__">Not Scanned</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Region</InputLabel>
          <Select value={filterRegion} label="Region" onChange={e => setFilterRegion(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {regionOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Group</InputLabel>
          <Select value={filterGroup} label="Group" onChange={e => setFilterGroup(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {groupOptions.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
        </FormControl>
        {activeFilters > 0 && (
          <Button size="small" variant="text" sx={{ fontSize: 12 }}
            onClick={() => { setFilterCdn(''); setFilterRegion(''); setFilterGroup(''); setSearch(''); }}>
            Clear filters
          </Button>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title={`Export ${displayed.length} filtered row${displayed.length !== 1 ? 's' : ''} to Excel`}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={displayed.length === 0}
              >
                Export Excel
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Re-run DNS CDN lookup for all instances. This may take a minute.">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                onClick={handleRefreshCdn}
                disabled={refreshing}
              >
                {refreshing ? 'Scanning…' : 'Refresh CDN'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Paper elevation={2}>
        {displayed.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CloudIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No instances match the selected filters.</Typography>
            {metrics.notScanned > 0 && !filterCdn && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {metrics.notScanned} instance{metrics.notScanned !== 1 ? 's have' : ' has'} not been scanned yet — click "Refresh All CDN" to run a DNS lookup.
              </Typography>
            )}
          </Box>
        ) : (
          <>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <SortCell col="Instance Name" label="Instance" />
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', py: 1 }}>Domain</TableCell>
                  <SortCell col="_cdn" label="CDN" />
                  <SortCell col="Region" label="Region" />
                  <SortCell col="Group" label="Group" />
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', whiteSpace: 'nowrap', py: 1 }}>Last Scanned</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', py: 1, width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((row, idx) => {
                  const cdn = getCdn(row);
                  const cdnStyle = CDN_COLORS[cdn] || null;
                  const name    = getName(row);
                  const altName = getAltName(row);
                  const domain  = getDomain(row);
                  const href    = domain ? (domain.startsWith('http') ? domain : `https://${domain}`) : null;
                  return (
                    <TableRow key={row._id || idx} hover>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                          {name || '—'}
                        </Typography>
                        {altName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                            {altName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {domain ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                              {domain}
                            </Typography>
                            {href && (
                              <IconButton size="small" component="a" href={href} target="_blank" rel="noopener" sx={{ p: 0.25, flexShrink: 0 }}>
                                <OpenInNewIcon sx={{ fontSize: 13, color: '#1976d2' }} />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {cdnStyle ? (
                          <Chip label={cdn} size="small"
                            sx={{ bgcolor: cdnStyle.bg, color: cdnStyle.text, fontWeight: 700, fontSize: 11, height: 22 }} />
                        ) : (
                          <Chip label="Not scanned" size="small" variant="outlined"
                            sx={{ fontSize: 10, height: 20, color: 'text.secondary', borderColor: 'divider' }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{row['Region'] || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{row['Group'] || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row._cdnUpdatedAt ? (
                          <Tooltip title={fmtDateTime(row._cdnUpdatedAt)} arrow placement="left">
                            <Typography variant="caption" color="text.secondary">
                              {relativeTime(row._cdnUpdatedAt)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 0.5 }}>
                        <Tooltip title="Re-scan CDN for this instance" arrow>
                          <span>
                            <IconButton
                              size="small"
                              disabled={refreshingRow !== null}
                              onClick={() => row._id && handleRefreshRow(row._id)}
                              sx={{ p: 0.5 }}
                            >
                              {refreshingRow === row._id
                                ? <CircularProgress size={14} />
                                : <RefreshIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {displayed.length > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <TablePagination
                component="div"
                count={displayed.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
          </>
        )}
      </Paper>

      {/* Regional breakdown modal */}
      <Dialog
        open={regionModal.open}
        onClose={() => setRegionModal(m => ({ ...m, open: false }))}
        maxWidth="xs"
        PaperProps={{ sx: { width: 360, maxWidth: '95vw' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: regionModal.color, flexShrink: 0 }} />
            <Typography variant="h6" component="span" sx={{ fontSize: '1rem', fontWeight: 700 }}>
              {regionModal.cdnLabel} — Regional Breakdown
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', ml: 3.5 }}>
            % share within this CDN type across all instances
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {modalBreakdown.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">No data available.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', py: 1 }}>Region</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', py: 1 }}>Instances</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', py: 1 }}>Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalBreakdown.map(({ region, count, pct: p }) => (
                  <TableRow key={region} hover>
                    <TableCell sx={{ py: 0.75 }}>{region}</TableCell>
                    <TableCell align="right" sx={{ py: 0.75, fontWeight: 600 }}>{count}</TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                        <Box sx={{ width: 48, height: 6, borderRadius: 1, bgcolor: '#f0f0f0', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${p}%`, bgcolor: regionModal.color, borderRadius: 1 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 30, textAlign: 'right' }}>{p}%</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: 12 }}>Total</TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontWeight: 700 }}>
                    {modalBreakdown.reduce((s, r) => s + r.count, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>100%</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRegionModal(m => ({ ...m, open: false }))}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CdnView;
